/**
 * Proposals Page
 *
 * Governance proposals hub — shows PriorityProposals moving through the
 * governance pipeline from Catchball through Ratification.
 *
 * Proposals are created via the Lift action on the SessionDetail Results tab.
 * Each proposal represents a ranked priority list lifted from one or more
 * sessions within a cycle.
 *
 * Visibility follows chain of custody: you see proposals where you are
 * the holder or tosser (or the proposal is published / shared with you).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ScrollText,
  Layers,
  Zap,
  Repeat,
  Infinity as InfinityIcon,
  ArrowRight,
  X,
  MessageSquare,
  ChevronRight,
  ChevronDown,
  CheckCircle,
} from 'lucide-react';
import { PageHeader } from '@/components/prioritization/PageHeader';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { usePinnedCycleStore } from '@/stores/prioritization/pinnedCycleStore';
import { usePriorityProposalStore } from '@/stores/prioritization/priorityProposalStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import {
  PROPOSAL_STATUS_CONFIG,
  canUserSeeProposal,
  type PriorityProposal,
  type PriorityProposalStatus,
} from '@/types/prioritization/priorityProposal';
import { StrategicNominationBadge } from '@/components/prioritization/governance/StrategicNominationBadge';

// ─── Pipeline steps ───────────────────────────────────────────────────────────

const PIPELINE_STEPS: { key: PriorityProposalStatus; label: string; number: number }[] = [
  { key: 'catchball', label: 'Catchball', number: 1 },
  { key: 'under_review', label: 'Under Review', number: 2 },
  { key: 'approved', label: 'Accepted', number: 3 },
  { key: 'ratified', label: 'Ratified', number: 4 },
];

// ─── Cadence badge ────────────────────────────────────────────────────────────

const CADENCE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  episodic: Zap,
  periodic: Repeat,
  continuous: InfinityIcon,
};

const CADENCE_COLORS: Record<string, string> = {
  episodic: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
  periodic: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  continuous: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
};

function CadenceBadge({ cadence }: { cadence?: string | Record<string, unknown> }) {
  // Resolve cadence to a plain mode string regardless of storage format
  // (JSONB may arrive as object, string, or stringified JSON)
  let key: string = 'periodic';
  if (typeof cadence === 'object' && cadence !== null) {
    key = ((cadence as Record<string, unknown>).mode as string) ?? 'periodic';
  } else if (typeof cadence === 'string') {
    const trimmed = cadence.trim();
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>;
        key = (parsed.mode as string) ?? 'periodic';
      } catch {
        key = trimmed;
      }
    } else {
      key = trimmed || 'periodic';
    }
  }
  const Icon = CADENCE_ICONS[key] ?? Repeat;
  const color = CADENCE_COLORS[key] ?? 'bg-[var(--surface)] text-[var(--text-sec)] border-[var(--rule)]';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${color}`}>
      <Icon className="h-3 w-3" />
      {key.charAt(0).toUpperCase() + key.slice(1)}
    </span>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-sm text-[var(--text-ter)] mt-0.5">{label}</div>
    </div>
  );
}

// ─── Pipeline visualization ───────────────────────────────────────────────────

function PipelineViz({ counts }: { counts: Partial<Record<PriorityProposalStatus, number>> }) {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide mb-4">
        Governance Pipeline
      </h2>
      <div className="flex items-center gap-2">
        {PIPELINE_STEPS.map((step, index) => {
          const config = PROPOSAL_STATUS_CONFIG[step.key];
          const count = counts[step.key] ?? 0;
          return (
            <div key={step.key} className="flex items-center gap-2 flex-1">
              <div
                className={`flex-1  border p-3 flex items-center justify-between ${
                  count > 0
                    ? 'border-[var(--rule)] bg-[var(--surface)]'
                    : 'border-[var(--rule)] bg-[var(--bg-raised)] opacity-50'
                }`}
              >
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                    {step.number}. {step.label}
                  </div>
                  <div className={`text-xl font-bold mt-0.5 ${count > 0 ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}`}>
                    {count}
                  </div>
                </div>
                {count > 0 && (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${config.badgeClass}`}>
                    {config.label}
                  </span>
                )}
              </div>
              {index < PIPELINE_STEPS.length - 1 && (
                <ArrowRight className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Proposal card ────────────────────────────────────────────────────────────

function ProposalCard({
  proposal,
  cycleName,
  cadence,
}: {
  proposal: PriorityProposal;
  cycleName?: string;
  cadence?: string | Record<string, unknown>;
}) {
  const [expanded, setExpanded] = useState(false);
  const config = PROPOSAL_STATUS_CONFIG[proposal.status];
  const hasNominated = proposal.rankedItems.some((i) => i.isStrategicallyNominated);
  const topItems = proposal.rankedItems.slice(0, expanded ? undefined : 5);
  const hasMore = proposal.rankedItems.length > 5;

  return (
    <div className="px-6 py-5 hover:bg-[var(--surface)]/60 transition-colors">
      {/* Header row */}
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-semibold text-[var(--text-pri)]">{proposal.title}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${config.badgeClass}`}>
              {config.label}
            </span>
            {hasNominated && <StrategicNominationBadge variant="icon" />}
          </div>
          <div className="flex items-center gap-4 text-xs text-[var(--text-ter)] flex-wrap">
            {cycleName && (
              <span className="flex items-center gap-1">
                <Layers className="h-3.5 w-3.5" />
                {cycleName}
              </span>
            )}
            {cadence && <CadenceBadge cadence={cadence} />}
            <span>
              {proposal.rankedItems.length} item{proposal.rankedItems.length !== 1 ? 's' : ''}
              {' · '}
              {proposal.sessionIds.length} session{proposal.sessionIds.length !== 1 ? 's' : ''}
            </span>
            <span>
              Lifted {proposal.liftedAt.toLocaleDateString()}
            </span>
            {proposal.catchballMessages.length > 0 && (
              <span className="flex items-center gap-1 text-[var(--text-ter)]">
                <MessageSquare className="h-3.5 w-3.5" />
                {proposal.catchballMessages.length} message{proposal.catchballMessages.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Rationale snippet */}
          {proposal.rationale && (
            <p className="mt-2 text-xs text-[var(--text-sec)] line-clamp-2">{proposal.rationale}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--hover-tint)] border border-[var(--rule)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            {expanded ? (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                Collapse
              </>
            ) : (
              <>
                <ChevronRight className="h-3.5 w-3.5" />
                Ranked List
              </>
            )}
          </button>
          {(proposal.status === 'catchball' || proposal.status === 'under_review') && (
            <Link
              to={`/prioritization/governance/proposals/${proposal.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] border border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors"
            >
              Review
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
          {(proposal.status === 'approved' || proposal.status === 'ratified') && (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)]">
              <CheckCircle className="h-3.5 w-3.5" />
              {proposal.status === 'ratified' ? 'Ratified' : 'Accepted'}
            </span>
          )}
        </div>
      </div>

      {/* Expandable ranked list */}
      {expanded && proposal.rankedItems.length > 0 && (
        <div className="mt-4 border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
          <div className="divide-y divide-[var(--rule)]">
            {topItems.map((item) => (
              <div
                key={item.itemId}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--surface)]"
              >
                <span className="w-6 text-center text-xs font-bold text-[var(--text-ter)] flex-shrink-0">
                  {item.rank}
                </span>
                <span className="flex-1 text-sm text-[var(--text-pri)]">{item.itemTitle}</span>
                {item.isStrategicallyNominated && (
                  <StrategicNominationBadge variant="icon" />
                )}
                {item.category && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-ter)]">
                    {item.category}
                  </span>
                )}
                {item.score !== undefined && (
                  <span className="text-xs font-mono text-[var(--text-ter)]">
                    {item.score.toFixed(1)}
                  </span>
                )}
              </div>
            ))}
          </div>
          {hasMore && !expanded && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="w-full py-2 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors border-t border-[var(--rule)]"
            >
              + {proposal.rankedItems.length - 5} more items
            </button>
          )}
        </div>
      )}

      {/* Open questions (shown when expanded) */}
      {expanded && proposal.openQuestions && (
        <div className="mt-3 px-4 py-3 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <p className="text-xs font-semibold text-[var(--text-sec)] mb-1 uppercase tracking-wide">
            Open Questions for Catchball
          </p>
          <p className="text-xs text-[var(--text-sec)]">{proposal.openQuestions}</p>
        </div>
      )}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-12 text-center">
      <div className="w-12 h-12 rounded-full bg-[var(--slate-dim)] flex items-center justify-center mx-auto mb-3">
        <ScrollText className="h-6 w-6 text-[var(--text-ter)]" />
      </div>
      <p className="text-sm font-medium text-[var(--text-sec)] mb-1">No proposals yet</p>
      <p className="text-sm text-[var(--text-ter)] max-w-xs mx-auto">
        Priority lists enter this lane when a facilitator uses the{' '}
        <span className="font-medium text-[var(--text-ter)]">Lift to Proposal</span> action
        on a completed session.
      </p>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ProposalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCycleId = searchParams.get('cycleId');
  const pinnedCycleId = usePinnedCycleStore((s) => s.pinnedCycleId);
  const pinnedCycleName = usePinnedCycleStore((s) => s.pinnedCycleName);
  const activeCycleId = urlCycleId ?? pinnedCycleId;

  const cycles = useDegapProcessStore((state) => state.cycles);
  const userId = useUserStore((s) => s.userId) ?? '';

  // Raw proposals from store — filtered in useMemo (Zustand safety)
  const allProposals = usePriorityProposalStore((s) => s.proposals);
  const fetchForCycle = usePriorityProposalStore((s) => s.fetchForCycle);

  // Load from Supabase whenever the active cycle changes.
  // When no cycle is selected, fetch all workspace proposals so nothing is missed.
  useEffect(() => {
    fetchForCycle(activeCycleId ?? undefined);
  }, [activeCycleId, fetchForCycle]);

  const activeCycleName = useMemo(
    () => cycles.find((c) => c.id === activeCycleId)?.name ?? pinnedCycleName ?? 'Selected cycle',
    [cycles, activeCycleId, pinnedCycleName],
  );

  // Filter to visible proposals for this user + optional cycle filter
  const visibleProposals = useMemo(() => {
    return allProposals.filter((p) => {
      const cycleMatch = activeCycleId ? p.cycleId === activeCycleId : true;
      return cycleMatch && canUserSeeProposal(p, userId);
    });
  }, [allProposals, activeCycleId, userId]);

  // Pipeline counts
  const pipelineCounts = useMemo(() => {
    const counts: Partial<Record<PriorityProposalStatus, number>> = {};
    for (const p of visibleProposals) {
      counts[p.status] = (counts[p.status] ?? 0) + 1;
    }
    return counts;
  }, [visibleProposals]);

  // Active (non-published, non-draft) proposals for the main list
  const activeProposals = useMemo(
    () =>
      visibleProposals.filter(
        (p) => p.status !== 'published' && p.status !== 'draft',
      ),
    [visibleProposals],
  );

  // Cycle lookup helpers
  const cycleById = useMemo(
    () => new Map(cycles.map((c) => [c.id, c])),
    [cycles],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ScrollText className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Proposals"
        subtitle="Priority lists in the governance lane — from Catchball through Ratification."
      />

      <div className="space-y-6">
        {/* Active cycle filter chip */}
        {activeCycleId && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--mustard)]" />
              Filtered to:{' '}
              <span className="font-semibold">{activeCycleName}</span>
              <button
                type="button"
                onClick={() => {
                  setSearchParams((p) => {
                    p.delete('cycleId');
                    return p;
                  });
                  usePinnedCycleStore.getState().exitWorkspace();
                }}
                className="ml-1 text-[var(--mustard)] hover:text-[var(--mustard)] transition-colors"
                title="Show all cycles"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
            <Link
              to="/prioritization/governance/proposals"
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              Show all cycles
            </Link>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard
            label="In Catchball"
            value={pipelineCounts.catchball ?? 0}
            color="text-[var(--text-ter)]"
          />
          <StatCard
            label="Under Review"
            value={pipelineCounts.under_review ?? 0}
            color="text-[var(--mustard)]"
          />
          <StatCard
            label="Accepted"
            value={pipelineCounts.approved ?? 0}
            color="text-[var(--text-ter)]"
          />
          <StatCard
            label="Ratified"
            value={pipelineCounts.ratified ?? 0}
            color="text-[var(--green)] dark:text-[var(--text-ter)]"
          />
        </div>

        {/* Pipeline visualization */}
        <PipelineViz counts={pipelineCounts} />

        {/* Proposals list */}
        {activeProposals.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            <div className="px-6 py-4 border-b border-[var(--rule)] flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">
                Active Proposals
              </h2>
              <span className="text-xs text-[var(--text-ter)]">
                {activeProposals.length} proposal{activeProposals.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="divide-y divide-[var(--rule)]">
              {activeProposals.map((proposal) => {
                const cycle = cycleById.get(proposal.cycleId);
                // Safely extract cadence mode — cycle.cadence may be a string, object, or JSON string
                const rawCadence = (cycle as { cadence?: unknown })?.cadence;
                const cadence: string =
                  typeof rawCadence === 'object' && rawCadence !== null
                    ? ((rawCadence as Record<string, unknown>).mode as string) ?? 'periodic'
                    : typeof rawCadence === 'string'
                    ? (() => {
                        try { return (JSON.parse(rawCadence) as Record<string, unknown>).mode as string ?? rawCadence; }
                        catch { return rawCadence; }
                      })()
                    : 'periodic';
                return (
                  <ProposalCard
                    key={proposal.id}
                    proposal={proposal}
                    cycleName={cycle?.name}
                    cadence={cadence}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Published proposals (collapsed section) */}
        {visibleProposals.some((p) => p.status === 'published') && (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden opacity-70">
            <div className="px-6 py-4 border-b border-[var(--rule)]">
              <h2 className="text-sm font-semibold text-[var(--text-sec)] uppercase tracking-wide">
                Published
                <span className="ml-2 text-xs font-normal text-[var(--text-ter)]">
                  {visibleProposals.filter((p) => p.status === 'published').length}
                </span>
              </h2>
            </div>
            <div className="divide-y divide-[var(--rule)]">
              {visibleProposals
                .filter((p) => p.status === 'published')
                .map((proposal) => {
                  const cycle = cycleById.get(proposal.cycleId);
                  const cadence =
                    (cycle as { mode?: string })?.mode ??
                    (cycle as { cadence?: string })?.cadence ??
                    'periodic';
                  return (
                    <ProposalCard
                      key={proposal.id}
                      proposal={proposal}
                      cycleName={cycle?.name}
                      cadence={cadence}
                    />
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
